/**
 * useAnonymousGroupChat
 * 
 * Manages group chat lifecycle for anonymous users
 * - Gets or creates a group chat for the anonymous session
 * - Links with pre-selected store staff members from store_office_chat_contacts
 * - Returns group_chat_id and loading state
 */

import { useEffect, useState, useCallback } from 'react';
import { getOrCreateAnonymousGroupChat, getGroupChatFromAnonymousSession } from '@/lib/supabase/chat';

interface UseAnonymousGroupChatOptions {
  anonymousSessionId?: string;
  storeId?: string;
}

interface UseAnonymousGroupChatResult {
  groupChatId: string | null;
  isCreating: boolean;
  error: Error | null;
}

export const useAnonymousGroupChat = ({
  anonymousSessionId,
  storeId,
}: UseAnonymousGroupChatOptions): UseAnonymousGroupChatResult => {
  const [groupChatId, setGroupChatId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!anonymousSessionId) {
      setIsCreating(false);
      return;
    }

    const initGroupChat = async () => {
      try {
        setIsCreating(true);
        setError(null);

        // First, try to get existing group chat
        const existingGroupId = await getGroupChatFromAnonymousSession(anonymousSessionId);
        
        if (existingGroupId) {
          setGroupChatId(existingGroupId);
          setIsCreating(false);
          return;
        }

        if (!storeId) {
          setError(new Error('No se pudo resolver el grupo del chat anónimo.'));
          setGroupChatId(null);
          setIsCreating(false);
          return;
        }

        // No existing chat, create new one with store staff members
        const result = await getOrCreateAnonymousGroupChat(anonymousSessionId, storeId);
        setGroupChatId(result.group_chat_id);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setGroupChatId(null);
      } finally {
        setIsCreating(false);
      }
    };

    initGroupChat();
  }, [anonymousSessionId, storeId]);

  return {
    groupChatId,
    isCreating,
    error,
  };
};
